import { MousePointerClick } from 'lucide-preact'
import type { BlastEngagementDto } from '@/types/emailBlast'
import { formatCount, formatRate } from './engagementFormat'
import { SectionHeading } from './sectionChrome'

const CLICKS_CAVEAT =
  'Reported by the email provider. It includes mail security scanners, which open every link ' +
  'automatically before delivering an email. Business inboxes do this routinely, so this number is ' +
  'usually much higher than the number of people.'

const OPEN_RATE_CAVEAT =
  'Apple Mail Privacy auto-loads the open pixel, so open rate is inflated. Treat confirmed visits as ' +
  'the reliable signal.'

const VISITS_HINT =
  'Someone opened the link and the page actually loaded in their browser. This is the number to trust.'

const SCANNERS_HINT =
  'Corporate mail security opens every link in an email before delivering it. These page loads came from ' +
  'that, not from a person. Normal, and nothing to act on.'

/**
 * Blast engagement (ORB-384, reworked ORB-706): a ladder rather than one headline number.
 *
 * The 2026-08-27 send reported a 41% click rate where not one human reached the landing page — every
 * measured click was a corporate link scanner. The obvious fix, splitting clicks into "real" and "bot",
 * cannot be computed: the correlation row carrying the bot label is only written for traffic that
 * PASSED classification, so the bot side is structurally zero, and the gap between the two numbers also
 * holds blocked page beacons, untracked CTAs and loads outside the token's TTL. Naming that gap "bots"
 * would swap one confident wrong number for another.
 *
 * So: confirmed visits lead, scanner visits sit beside them as a measured counterpart, and the
 * provider's click count is demoted and caveated. All three are counted, none is derived — the gap
 * between recorded clicks and the two visit figures is genuinely unknown, and subtracting to fill it
 * would put a precise number on a guess.
 *
 * The word "bot" appears nowhere. To an operator it reads as an attack, when the truth is mundane and
 * universal: mail security opens every link before delivery.
 */
export function BlastEngagementSection({ engagement }: { engagement: BlastEngagementDto | null | undefined }) {
  const hasData = !!engagement && engagement.Delivered > 0

  return (
    <section className="space-y-4">
      <SectionHeading>Engagement</SectionHeading>

      {!hasData ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Nothing delivered yet — engagement appears once delivery starts. Rates show “—”, never a
          misleading 0%.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <Measure
              value={formatCount(engagement!.ConfirmedVisits)}
              label="confirmed visits"
              size="lg"
              privileged
              caveat={VISITS_HINT}
            />
            <Measure value={formatRate(engagement!.VisitRatePercent)} label="visit rate" privileged />
            <Measure
              value={formatCount(engagement!.AutomatedVisits)}
              label="security scanners"
              muted
              caveat={SCANNERS_HINT}
            />
          </div>

          <hr className="border-slate-200 dark:border-slate-700" />

          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <Measure
              value={formatCount(engagement!.ClickedDistinct)}
              label="link clicks recorded"
              muted
              caveat={CLICKS_CAVEAT}
            />
            <Measure value={formatRate(engagement!.OpenRatePercent)} label="open rate" muted caveat={OPEN_RATE_CAVEAT} />
            <Measure value={formatRate(engagement!.BounceRatePercent)} label="bounce rate" muted />
          </div>

          {engagement!.ApproxUnsubscribesInWindow != null && (
            <p className="text-xs text-slate-400 dark:text-slate-500">
              <span className="font-medium text-slate-500 dark:text-slate-400">
                {engagement!.ApproxUnsubscribesInWindow.toLocaleString()}
              </span>{' '}
              {engagement!.ApproxUnsubscribesInWindow === 1 ? 'recipient' : 'recipients'} unsubscribed within{' '}
              {engagement!.UnsubscribeWindowDays} days of this send (from the suppression list; approximate
              attribution). Campaign-level unsubscribe rate is on the campaign Broadcasts tab.
            </p>
          )}

          {engagement!.TopClickedLinks.length > 0 && (
            <div className="pt-1">
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                <MousePointerClick className="h-3.5 w-3.5" /> Top clicked links
                <span className="font-normal normal-case text-slate-400 dark:text-slate-500">
                  · last click per recipient
                </span>
              </h4>
              <ul className="space-y-1.5 text-sm">
                {engagement!.TopClickedLinks.map(link => (
                  <li key={link.Url} className="flex items-center justify-between gap-3">
                    <span className="truncate text-slate-700 dark:text-slate-300" title={link.Url}>
                      {link.Url}
                    </span>
                    <span className="shrink-0 font-medium tabular-nums text-slate-500 dark:text-slate-400">
                      {link.Count.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Measure({
  value,
  label,
  size = 'md',
  privileged = false,
  muted = false,
  caveat,
}: {
  value: string
  label: string
  size?: 'md' | 'lg'
  privileged?: boolean
  muted?: boolean
  caveat?: string
}) {
  const valueClass = privileged
    ? `${size === 'lg' ? 'text-3xl' : 'text-2xl'} font-semibold tabular-nums text-primary-700 dark:text-primary-400`
    : muted
    ? 'text-xl font-medium tabular-nums text-slate-500 dark:text-slate-400'
    : 'text-2xl font-semibold tabular-nums text-slate-900 dark:text-white'
  const labelClass = privileged
    ? 'text-xs uppercase tracking-wide text-primary-700/70 dark:text-primary-400/70'
    : 'text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500'
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={valueClass}>{value}</span>
      <span
        className={caveat ? `${labelClass} cursor-help underline decoration-dotted underline-offset-2` : labelClass}
        title={caveat}
      >
        {label}
        {caveat ? ' ⓘ' : ''}
      </span>
    </div>
  )
}
